import { createPortal } from 'react-dom'
import { useGameStore, type ActivityLogEntry } from '@/store/gameState'
import { PLACES } from '@/data/places'
import { ECHOES } from '@/data/echoes'
import { HUNTS } from '@/data/hunts'
import styles from './ActivityLogModal.module.css'

interface ActivityLogModalProps {
  onClose: () => void
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now()
  const then = new Date(timestamp).getTime()
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(timestamp).toLocaleDateString()
}

function FocusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function HuntIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
      <circle cx="11" cy="11" r="3" />
    </svg>
  )
}

function TravelIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function EmptyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" opacity="0.3" />
      <path d="M12 6v6l4 2" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.5" />
    </svg>
  )
}

function LogEntry({ entry, index }: { entry: ActivityLogEntry; index: number }) {
  const renderContent = () => {
    switch (entry.type) {
      case 'focus': {
        const label = entry.wasEarlyStop
          ? `${entry.actualMinutes}/${entry.duration}min`
          : `${entry.duration}min Focus`
        const decayNote = entry.decay > 0 ? ` (-${entry.decay} decay)` : ''
        return (
          <div className={styles.entryContent}>
            <div className={styles.entryMain}>
              <span className={styles.entryTitle}>{label}</span>
              {entry.wasEarlyStop && <span className={styles.earlyTag}>Early</span>}
            </div>
            <div className={styles.entryMeta}>
              <span className={`${styles.energyBadge} ${styles.energyEarned}`}>
                +{entry.energyEarned} <span className={styles.bolt}>⚡</span>
              </span>
              {decayNote && <span className={styles.decayNote}>{decayNote}</span>}
            </div>
          </div>
        )
      }
      case 'hunt': {
        const huntLabel = HUNTS[entry.huntType]?.label ?? entry.huntType
        const echoName = entry.echoId ? ECHOES[entry.echoId]?.name : null
        return (
          <div className={styles.entryContent}>
            <div className={styles.entryMain}>
              <span className={styles.entryTitle}>{huntLabel}</span>
              {entry.isNewDiscovery && <span className={styles.newTag}>New!</span>}
              {entry.isGlimmering && <span className={styles.glimmerTag}>✨</span>}
            </div>
            <div className={styles.entryMeta}>
              <span className={`${styles.energyBadge} ${styles.energySpent}`}>
                -{entry.energySpent} <span className={styles.bolt}>⚡</span>
              </span>
              {entry.success && echoName ? (
                <span className={styles.resultSuccess}>Found {echoName}</span>
              ) : (
                <span className={styles.resultFail}>Nothing found</span>
              )}
            </div>
          </div>
        )
      }
      case 'travel': {
        const fromName = PLACES[entry.fromPlace]?.name ?? entry.fromPlace
        const toName = PLACES[entry.toPlace]?.name ?? entry.toPlace
        return (
          <div className={styles.entryContent}>
            <div className={styles.entryMain}>
              <span className={styles.entryTitle}>Traveled</span>
            </div>
            <div className={styles.entryMeta}>
              <span className={`${styles.energyBadge} ${styles.energySpent}`}>
                -{entry.energySpent} <span className={styles.bolt}>⚡</span>
              </span>
              <span className={styles.travelRoute}>
                {fromName} <span className={styles.arrow}>→</span> {toName}
              </span>
            </div>
          </div>
        )
      }
    }
  }

  const iconClass = `${styles.entryIcon} ${styles[entry.type]} ${
    entry.type === 'hunt' && !entry.success ? styles.huntFail : ''
  }`

  return (
    <div
      className={styles.entry}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className={iconClass}>
        {entry.type === 'focus' && <FocusIcon />}
        {entry.type === 'hunt' && <HuntIcon />}
        {entry.type === 'travel' && <TravelIcon />}
      </div>
      {renderContent()}
      <div className={styles.entryTime}>{formatRelativeTime(entry.timestamp)}</div>
    </div>
  )
}

export function ActivityLogModal({ onClose }: ActivityLogModalProps) {
  const activityLog = useGameStore((s) => s.activityLog)

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Activity Log</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          {activityLog.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <EmptyIcon />
              </div>
              <p className={styles.emptyTitle}>No activity yet</p>
              <p className={styles.emptyHint}>
                Complete focus sessions, hunt for Echoes, or travel to new places to see your journey here.
              </p>
            </div>
          ) : (
            <div className={styles.list}>
              {activityLog.map((entry, index) => (
                <LogEntry key={entry.id} entry={entry} index={index} />
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerText}>{activityLog.length} entries</span>
        </div>
      </div>
    </div>,
    document.body
  )
}
